import useSWRImmutable from "swr/immutable";
import { Button } from "@/components/Button/Button";
import Dialog from "@/components/Dialog/Dialog";
import { Icon } from "@/components/Icon";
import { DialogClose } from "@radix-ui/react-dialog";
import { useContext, useState } from "react";
import { Slot } from "utils/create-slots";
import Widget from "../Widget/Widget";
import { CalculatorContext } from "./CalculatorContext";
import CalculatorSelect from "./CalculatorSelect";
import CalculatorInput from "./CalculatorInput";

import CalculatorMonthSelector from "./CalculatorMonthSelector";
import CalculatorStepIndicator from "./CalculatorStepIndicator";
import CalculatorSummary from "./CalculatorSummary";
import CalculatorFinancingTypeSelector from "./CalculatorFinancingTypeSelector";
import CalculatorCustomerTypeSelector from "./CalculatorCustomerTypeSelector";
import CalculatorInfoLayer from "./CalculatorInfoLayer";
import CalculatorErrorDisplay from "./CalculatorErrorDisplay";
import CalculatorModalFooter from "./CalculatorModalFooter";

import {
  elementValuesFinance,
  elementValuesLeasing,
  elementValuesCustomerType,
  elementValuesCalculatorType,
} from "./data/elementValues";
import { formatPrice, formatPercentage } from "./lib/formatFunctions";
import { useIsomorphicLayoutEffect } from "hooks/useIsomorphicLayoutEffect";
import * as gtag from "@/lib/gtag";

const fetcher = (url) => fetch(url).then((r) => r.json());

const CalculatorRoot = ({ data }) => {
  const [calculatorUsed, setCalculatorUsed] = useState(false);
  const { calculatorParams, setCalculatorParams } =
    useContext(CalculatorContext);

  const [nonReactiveValues, setNonReactiveValues] = useState(0 || "0");

  useIsomorphicLayoutEffect(() => {
    if (!data || !data.preis) return;

    setCalculatorParams((prevParams) => ({
      ...prevParams,
      purchasePrice: data.preis,
      schwackecode: data.schwackecode,
      customerType: data.showNettoPreis ? "business" : "private",
      car: data.internalid || "No internal ID",
      deposit: data.preis * 0.3 || 4000,
      monthlyRate: data.showNettoPreis
        ? data.nettoMonatlicherate
        : data.monatlicherate || 315,
      monthlyRateNet: data.nettoMonatlicherate || 315,
      monthlyRateGross: data.monatlicherate || 375,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRateDisplayInFooter = (dynamicMonthlyRate, customerType) => {
    let rate = "0";

    if (customerType === "business" && data.showNettoPreis === true) {
      if (dynamicMonthlyRate.monthlyInstallmentTotalAmount) {
        rate = formatPrice(
          dynamicMonthlyRate?.monthlyInstallmentTotalAmount?.netValue
        );
        if (typeof window !== undefined) {
          window.localStorage.setItem(
            "calculatorDataRate",
            dynamicMonthlyRate?.monthlyInstallmentTotalAmount?.netValue
          );
          window.localStorage.setItem(
            "monthlyRateNet",
            dynamicMonthlyRate?.monthlyInstallmentTotalAmount?.netValue
          );
        }
      } else {
        rate = formatPrice(dynamicMonthlyRate?.monthlyInstallment?.netValue);
        if (typeof window !== undefined) {
          window.localStorage.setItem(
            "calculatorDataRate",
            dynamicMonthlyRate?.monthlyInstallment?.netValue
          );
          window.localStorage.setItem(
            "monthlyRateNet",
            dynamicMonthlyRate?.monthlyInstallment?.netValue
          );
        }
      }
    } else if (customerType === "private" || data.showNettoPreis === false) {
      if (dynamicMonthlyRate?.monthlyInstallmentTotalAmount) {
        rate = formatPrice(
          dynamicMonthlyRate?.monthlyInstallmentTotalAmount?.grossValue
        );
        if (typeof window !== undefined) {
          window.localStorage.setItem(
            "calculatorDataRate",
            dynamicMonthlyRate?.monthlyInstallmentTotalAmount?.grossValue
          );
          window.localStorage.setItem(
            "monthlyRateGross",
            dynamicMonthlyRate?.monthlyInstallmentTotalAmount?.grossValue
          );
        }
      } else {
        rate = formatPrice(dynamicMonthlyRate?.monthlyInstallment?.grossValue);
        if (typeof window !== undefined) {
          window.localStorage.setItem(
            "calculatorDataRate",
            dynamicMonthlyRate?.monthlyInstallment?.grossValue
          );
          window.localStorage.setItem(
            "monthlyRateGross",
            dynamicMonthlyRate?.monthlyInstallment?.grossValue
          );
        }
      }
    }
    if (rate) {
      if (typeof window !== undefined) {
        window.localStorage.setItem("calculatorDataRate", rate);
        setNonReactiveValues(rate);
      }
    }
  };

  const getKey = (params) => {
    if (!params.type || !calculatorUsed) return null;

    const baseUrl = process.env.NEXT_PUBLIC_API_URL + "finance-calculator/";
    const query = new URLSearchParams({
      type: params.type,
      term: params.term,
      deposit: params.deposit || "0",
      purchasePrice: params.purchasePrice,
      contractualMileage: params.contractualMileage,
      car: data.uid || "1234",
    }).toString();
    return `${baseUrl}?${query}`;
  };

  const {
    data: responseData,
    error,
    isValidating: loading,
  } = useSWRImmutable(getKey(calculatorParams), fetcher);

  const handleCalculatorUsage = () => {
    setCalculatorUsed(true);
  };
  const clearCalculatorData = () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("calculatorData");
    }
  };

  useIsomorphicLayoutEffect(() => {
    if (!data.isleasable) {
      setCalculatorParams((prevParams) => ({
        ...prevParams,
        type: "financing",
        calculatorType: "financing",
        term: 36,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calcData = responseData?.response || {};

  const handleTracking = () => {
    if (!data) return;

    gtag.event({
      event: "begin_checkout",
      ecommerce: {
        transaction_id:
          `T_${data?.internalid}_${Math.floor(Math.random() * 1000)}` || "",
        currency: "EUR",
        value: `${data?.preis || 0}`,
        monthly_value: `${nonReactiveValues || 0}`,
        financing: `${calculatorParams.type || "no type"}`,
        duration: `${calculatorParams.term || "no term"}`,
        mileage_per_year: `${
          calculatorParams.contractualMileage || "no mileage"
        }`,
        items: [
          {
            item_id: data?.internalid || "",
            item_name: data?.title || "",
            item_brand: data?.marke || "",
            item_category: data?.vehicleType || "",
            item_category2: data?.usagecategory || "",
            item_category3: data?.getriebeart || "",
            item_category4: data?.kraftstoffart || "",
            item_variant: data?.farbe || "",
            price: data?.preis || "",
            quantity: 1,
          },
        ],
      },
    });
  };

  useIsomorphicLayoutEffect(() => {
    handleRateDisplayInFooter(calcData, calculatorParams.customerType);
  }, [calcData, calculatorParams.customerType]);

  if (!data || !data.preis) return null;
  if (error)
    return (
      <p>
        Es ist leider ein Fehler aufgetreten. Versuchen Sie es bitte zu einem
        späteren Zeitpunkt erneut.
      </p>
    );

  return (
    <Dialog
      position="center"
      width="medium"
      className=" md:h-[auto] h-[100dvh] scrollbar-hide !p-0 md:!max-w-[95vw] xl:!max-w-[1440px] lg:!max-w-[1100px] 2xl:min-h-[866px] xl:w-[85vw] lg:w-[90vw] md:aspect-[5/4]"
      showDefaultDialogClose={false}
      onOpenChange={() => clearCalculatorData()}
    >
      <Slot name="trigger">
        <Widget
          isfinancable={data.isfinancable}
          data={data}
          onClick={() => handleCalculatorUsage()}
        >
          <Slot name="modalTrigger">
            <Widget.ModalTrigger />
          </Slot>
        </Widget>
      </Slot>
      <Slot name="content">
        <div className="lg:shadow-transparent shadow-[0px_2px_15px_#acabab] sticky lg:p-7  lg:bg-white bg-gray-100 top-0 z-[999] lg:w-12 lg:h-12 w-full flex lg:flex-row flex-row-reverse lg:justify-end justify-around ml-auto h-12  ">
          <div className="flex lg:flex-col flex-row [&>button]:flex [&>button]:justify-end items-center ">
            <DialogClose aria-label="Close">
              <Icon
                type="close"
                purposeLabel="Schliessen"
                className="w-6 h-6 hover:text-blue-light "
              />
            </DialogClose>
          </div>
          <div className="lg:hidden flex justify-center items-center">
            <p className="text-2xl font-bold whitespace-nowrap">
              Finanzierungsangebot berechnen
            </p>
          </div>
        </div>
        <div className="2xl:max-h-[800px] lg:max-h-[600px] relative lg:-mt-8 ">
          <div className="flex flex-col md:p-10 md:pt-0 pt-7 lg:px-8 2xl:px-15  px-6 md:pb-[40px]">
            <div className="lg:flex hidden justify-center mb-7">
              <p className="text-5xl font-bold">
                Finanzierungsangebot berechnen
              </p>
            </div>
            <CalculatorStepIndicator
              step="1"
              label="Wie möchten Sie Ihr Fahrzeug finanzieren?"
            />

            <CalculatorCustomerTypeSelector
              properties={elementValuesCustomerType}
              divider={true}
            />
            <CalculatorFinancingTypeSelector
              properties={elementValuesCalculatorType}
              divider={true}
              isleasable={data.isleasable}
              depositBasePrice={data.preis || 4000}
            />
            <CalculatorStepIndicator step="2" label="Laufzeit" />
            <CalculatorMonthSelector
              properties={[elementValuesLeasing, elementValuesFinance]}
            />
            <div className="grid lg:grid-cols-2 grid-cols-1 gap-8 mb-2">
              <CalculatorInput
                max={data.preis}
                paramType="deposit"
                label={`${
                  calculatorParams.type === "leasing"
                    ? "Sonderzahlung:"
                    : "Anzahlung:"
                }`}
                sublabel={`
                ${
                  calculatorParams.type === "leasing"
                    ? "Sonderzahlungssumme: ("
                    : "Anzahlungssumme: ("
                }`}
              />
              <CalculatorSelect
                erstzulassung={data.ezCalculator}
                kilometer={data.kilometer}
              />
            </div>

            <CalculatorStepIndicator
              step="3"
              label={`${
                calculatorParams.type === "leasing"
                  ? "Ihr Leasingangebot:"
                  : "Ihr Ballonkreditangebot:"
              }`}
            />
            <CalculatorErrorDisplay error={responseData} />
            {(responseData as any)?.errorCode === 400 && (
              <CalculatorInfoLayer />
            )}
            <CalculatorSummary data={calcData} carData={data} />

            {(responseData as any)?.errorCode ? null : (
              <div className="flex justify-center mt-8">
                <a
                  className="!no-underline flex text-blue-light hover:text-blue-medium"
                  href={`${
                    process.env.NEXT_PUBLIC_API_URL
                  }print-financing/?${new URLSearchParams({
                    ...calculatorParams,

                    effectiveInterestRate:
                      (calcData as any)?.effectiveInterestRate !== undefined &&
                      (calcData as any)?.effectiveInterestRate !== null
                        ? (calcData as any).effectiveInterestRate
                        : "interestRate",
                    car: data.uid,
                    monthlyRate: nonReactiveValues || 0,
                    finalInstallment: (calcData as any)?.balloon?.grossValue
                      ? formatPrice((calcData as any)?.balloon?.grossValue)
                      : 0,
                    totalRepayAmount:
                      typeof (calcData as any)?.totalRepaymentAmount ===
                      "object"
                        ? formatPrice(
                            (calcData as any)?.totalRepaymentAmount.grossValue
                          )
                        : formatPrice(
                            (calcData as any)?.totalRepaymentAmount
                          ) || 0,

                    netLoanAmount:
                      formatPrice((calcData as any)?.netLoanAmount) || 0,
                  }).toString()}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon type="download" className="w-8 h-8 mr-4" />
                  <span className="text-3xl">Als PDF herunterladen</span>
                </a>
              </div>
            )}

            <div className="flex justify-center mt-8">
              <Button
                as="a"
                rel="nofollow"
                disabled={true}
                className="cursor-pointer lg:!text-sm xl:!text-2xl hd:!text-3xl w-[360px] !font-semibold lg:mb-0 mb-10 "
                onClick={() => handleTracking()}
                href={data.link.request}
              >
                <Icon type="mail" className="w-6 h-6 mr-4" />
                Kauf anfragen
              </Button>
            </div>
          </div>
          <CalculatorModalFooter
            loading={loading}
            error={error}
            data={data}
            dynamicMonthlyRate={nonReactiveValues}
            dynamicInterestRate={
              (calcData as any)?.effectiveInterestRate !== undefined &&
              (calcData as any)?.effectiveInterestRate !== null
                ? (calcData as any).effectiveInterestRate === 0
                  ? formatPercentage(0)
                  : formatPercentage((calcData as any).effectiveInterestRate)
                : "interestRate"
            }
          />
        </div>
      </Slot>
    </Dialog>
  );
};

export default CalculatorRoot;
